'use client';

import { c as _c } from "react/compiler-runtime";
import { use } from 'react';
import PropTypes from 'prop-types';
import { useStore } from 'jotai';
import { useHydrateAtoms } from 'jotai/utils';
import { DEFAULT_WAREHOUSE_ID } from 'commons/constants';
import warehouseIdForUserAtom from './warehouseIdForUserAtom';

/**
 * Seeds warehouseIdForUserAtom with the warehouse the server resolved from the
 * visitor's zip cookie, so the server render and the first client render agree
 * (CON-8558). Without this the atom starts at DEFAULT_WAREHOUSE_ID on the
 * client no matter what the server rendered.
 *
 * There is one Jotai store per page load (JotaiProvider is mounted in
 * AllProviders from the root layout with no `store` prop, and never remounts on
 * client navigation), so this hydrates once. Deliberately hydrate-only, never
 * `dangerouslyForceHydrate`: on a soft navigation from another page the
 * client-resolved value must win over this late-mounting hydrator.
 *
 * Wraps its readers rather than sitting beside them - useHydrateAtoms only wins
 * over a reader that mounts after it, and as a sibling that ordering would rest
 * on nothing but the order the elements happen to appear in.
 */
const WarehouseIdHydrator = t0 => {
  const $ = _c(6);
  const {
    warehouseId,
    warehouseIdPromise,
    children
  } = t0;
  const resolvedWarehouseId = warehouseIdPromise ? use(warehouseIdPromise) : warehouseId;
  const t1 = useStore();
  let t2;
  if ($[0] !== t1) {
    t2 = t1.get(warehouseIdForUserAtom);
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const currentWarehouseId = t2;
  const clientAlreadyChoseWarehouse = currentWarehouseId != null && String(currentWarehouseId) !== String(DEFAULT_WAREHOUSE_ID);
  const resolvedIsFallback = resolvedWarehouseId != null && String(resolvedWarehouseId) === String(DEFAULT_WAREHOUSE_ID);
  let t3;
  if ($[2] !== clientAlreadyChoseWarehouse || $[3] !== resolvedIsFallback || $[4] !== resolvedWarehouseId) {
    t3 = resolvedWarehouseId == null || resolvedIsFallback && clientAlreadyChoseWarehouse ? [] : [[warehouseIdForUserAtom, resolvedWarehouseId]];
    $[2] = clientAlreadyChoseWarehouse;
    $[3] = resolvedIsFallback;
    $[4] = resolvedWarehouseId;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  useHydrateAtoms(t3);
  return children;
};
WarehouseIdHydrator.propTypes = {
  warehouseId: PropTypes.string,
  warehouseIdPromise: PropTypes.object,
  children: PropTypes.node
};
export default WarehouseIdHydrator;